import * as _ from "lodash";
import * as Papa from "papaparse";
import ICsvData from "../models/ICsvData";
import RowValidation from "../models/RowValidation";

export const cowNumberColumns = [
  "Cow number/name",
  "Cow number",
  "Cow name",
  "Cow number or name",
  "Cow name or number",
];
export const sireColumn = "Cow Sire";
export const grandsireColumn = "Maternal Grandsire";

// Convert csv data to a list of cows
export function csvToCows(data: any): ICsvData[] {
  // Parse csv data
  const results = Papa.parse(data, {
    skipEmptyLines: true,
  });

  // check any errors from papaparse package
  if (!_.isEmpty(results.errors)) {
    return convertCsvErrors(results.errors);
  }

  // check the order of columns
  // the first row is header
  if (!isColumnOrdered(results.data[0])) {
    const error = {
      message: "Column order must be cow number, cow sire, maternal grandsire",
    };

    return convertCsvErrors([error]);
  }

  // no row exist
  if (results.data.length === 1) {
    const error = {
      message: "No row found",
    };

    return convertCsvErrors([error]);
  }

  const cows: ICsvData[] = [];

  // start from the second row
  for (let i = 1; i < results.data.length; i += 1) {
    const cowNumber = _.trim(results.data[i][0]);
    const sire =
      _.trim(results.data[i][1]) === ""
        ? undefined
        : _.toNumber(results.data[i][1]);
    const grandSire =
      _.trim(results.data[i][2]) === ""
        ? undefined
        : _.toNumber(results.data[i][2]);
    const messages: string[] = [];

    // check cow number
    if (cowNumber === undefined || _.isEmpty(cowNumber)) {
      messages.push("Cow missing");
    }

    // check sire
    if (sire === undefined || _.isNaN(sire)) {
      messages.push("Cow sire missing");
    }

    // check grandsire
    if (grandSire === undefined || _.isNaN(grandSire)) {
      messages.push("Maternal grandsire missing");
    }

    const cow: ICsvData = {
      cowNumber,
      grandSire,
      id: i,
      messages,
      row: i,
      sire,
      validation:
        messages.length === 0 ? RowValidation.Valid : RowValidation.Warning,
    };

    cows.push(cow);
  }
  return cows;
}

// generate an example csv data
export function getExampleCsv() {
  return [
    // use Cow number/name as default title for example csv
    [cowNumberColumns[0], sireColumn, grandsireColumn],
    ["abc123", "112080", "108187"],
  ];
}

// convert csv errors to ICsvData
export function convertCsvErrors(errors: any): ICsvData[] {
  const csvErrors: ICsvData[] = [];

  for (let i = 0; i < errors.length; i += 1) {
    csvErrors.push({
      id: i,
      messages: [`Invalid CSV file - ${errors[i].message}`],
      row: errors[i].row,
      validation: RowValidation.Error,
    });
  }

  return csvErrors;
}

// check whether the file is a csv file
export function isCsvFile(file: any): boolean {
  const isEndsWithCsv = _.endsWith(file.name, "csv");

  // all valid csv mime type according to http://filext.com/file-extension/CSV
  const validCsvTypes = [
    "text/comma-separated-values",
    "text/csv",
    "application/csv",
    "application/excel",
    "application/vnd.ms-excel",
    "application/vnd.msexcel",
    "text/anytext",
  ];

  const isCsvType = !_.isNil(
    _.find(validCsvTypes, (type) => type === file.type)
  );

  return isEndsWithCsv && isCsvType;
}

// get all error or warning rows
export function getRowErrors(csvData: ICsvData[]): ICsvData[] {
  const errors: ICsvData[] = _.filter(
    csvData,
    (data) => data.validation !== RowValidation.Valid
  );
  return errors;
}

// validate the order of columns
export function isColumnOrdered(columns: string[]): boolean {
  if (columns.length < 3) {
    return false;
  }

  const cowNumberColumn = _.toLower(_.trim(columns[0]));
  const isCowNumberColumnValid = !_.isNil(
    _.find(cowNumberColumns, (c) => cowNumberColumn === _.toLower(c))
  );
  const isSireColumnValid =
    _.toLower(_.trim(columns[1])) === _.toLower(sireColumn);
  const isGrandsireColumnValid =
    _.toLower(_.trim(columns[2])) === _.toLower(grandsireColumn);

  return isCowNumberColumnValid && isSireColumnValid && isGrandsireColumnValid;
}

// Convert internal inbreeding data to CSV
export function inbreedingToCsv(origData: any, keys: string[]) {
  const data = _.cloneDeep(origData);

  // replace array of error messages with standard error messages
  data.forEach((item: any) => {
    item.Issues = convertMultipleIssuesToSingleIssueString(item);
  });

  const results = Papa.unparse({
    fields: keys,
    data,
  });

  return results;
}

function convertMultipleIssuesToSingleIssueString(item: any) {
  const issues = item.Issues;

  if (issues.length > 1) {
    if (issues.indexOf("Cow missing") >= 0) {
      // all possible scenarios with cow missing
      if (issues.indexOf("Not on LIC bull list") >= 0) {
        return "Cow missing and Cow sire or maternal grandsire not on LIC bull list";
      }
      if (
        issues.indexOf("Cow sire missing") >= 0 &&
        issues.indexOf("Maternal grandsire missing") >= 0
      ) {
        return "Cow number not found / Bull not found";
      }
      if (
        issues.indexOf("Cow sire missing") >= 0 &&
        issues.indexOf("Not on LIC bull list") >= 0
      ) {
        return "Cow and cow sire missing and maternal grandsire not on LIC bull list";
      }
      if (
        issues.indexOf("Maternal grandsire missing") >= 0 &&
        issues.indexOf("Not on LIC bull list") >= 0
      ) {
        return "Cow and maternal grandsire missing and cow sire not on LIC bull list";
      }
      if (issues.indexOf("Maternal grandsire missing") >= 0) {
        return "Cow and maternal grandsire missing";
      }
      if (issues.indexOf("Cow sire missing") >= 0) {
        return "Cow and cow sire missing";
      }
      if (issues.indexOf("Not on LIC bull list") >= 0) {
        return "Cow missing and bull(s) not on LIC bull list";
      }
    } else if (issues.indexOf("Cow sire missing") >= 0) {
      // all possible scenarios with cow ok and sire missing
      if (issues.indexOf("Maternal grandsire missing") >= 0) {
        return "Cow sire and maternal grandsire missing";
      }
      if (issues.indexOf("Not on LIC bull list") >= 0) {
        return "Cow sire missing and maternal grandsire not on LIC bull list";
      }
    } else if (issues.indexOf("Maternal grandsire missing") >= 0) {
      // all possible scenarios with cow ok and grandsire missing
      if (issues.indexOf("Not on LIC bull list") >= 0) {
        return "Maternal grandsire missing and cow sire not on LIC bull list";
      }
    }
  }

  if (issues.length === 1) {
    if (issues[0] === "Not on LIC bull list") {
      return "Cow sire or maternal grandsire not on LIC bull list";
    }

    // we have only 1 error message
    return issues[0];
  }

  return "";
}



// WEBPACK FOOTER //
// ./src/scripts/utils/CsvHelpers.ts